/**
 * How the partner will see one plan step.
 *
 * This is the thing that makes approval mean something. "Approve the plan" without it asks the
 * author to sign off on a sentence in a review screen; with it they are looking at the card that
 * will appear on somebody else's phone, in the panel it will appear in, with the same words and
 * the same "from the game" line under it.
 *
 * Deliberately non-interactive and deliberately not pixel-perfect: the frame is dashed, captioned
 * `Preview · not live`, inert to every press, and its controls are drawn at 60% — because a
 * preview that could be mistaken for the real thing is a preview somebody will try to press.
 *
 * It also owns the other reading of a phrasing: `diffWords` / `MarkedPhrasing`, which say what a
 * redraft changed. Same subject — the words the partner will see — from the other side, so the two
 * live together rather than the review screen growing its own copy.
 *
 * APP-LOCAL COMPOSITION, not a design-system component, and deliberately absent from
 * `_design.md`'s components: catalogue. It is a mock of THIS app's panels, keyed on this app's
 * step types, drawn with Badge, Button, Text and Points — every one of which the system names.
 * A generic "preview a card" component would have nothing left in it once those came out.
 */
import { Points } from '@/components/points';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { useFieldEdge } from '@/lib/edges';
import { cn } from '@/lib/utils';
import * as React from 'react';
import { View } from 'react-native';

export interface PreviewStep {
  panel: string;
  phrasing: string;
  payload: Record<string, unknown> | null;
}

const str = (value: unknown, fallback = ''): string =>
  typeof value === 'string' && value.trim() ? value : fallback;
const num = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

/** Which panel of theirs it lands in — the label above the preview. */
const WHERE: Record<string, string> = {
  mission: 'Missions',
  list_item: 'Our List',
  date_card: 'Date Night',
  sync_probe: 'Sync',
  gratitude_nudge: 'Gratitude',
  coop_challenge: 'Missions & Scoreboard',
};

export function StepPreview({ step, partnerName }: { step: PreviewStep; partnerName: string }) {
  const payload = step.payload ?? {};
  // The dashed frame IS the "this is a mock" signal, and --border against --card is 1.45:1 in the
  // Tuxedo dark room: the frame simply was not there, which is half of why the mock buttons inside
  // it read as live ones.
  const edge = useFieldEdge();

  return (
    /**
     * INERT AT THE FRAME, not at each control.
     *
     * `disabled` on the two mock buttons made them look off but still answer a press with a focus
     * ring, and nothing at all stopped a finger landing on the checkbox square or the sealed card.
     * `pointerEvents="none"` on the whole frame is the honest version: nothing in here is a
     * control, so nothing in here takes a press.
     */
    <View pointerEvents="none" className={cn('gap-2 rounded-md border border-dashed p-3', edge)}>
      {/* The caption says what the box IS before it says what it shows. "How Gabi sees it" alone
          is a promise about accuracy, which a reader can take as "this is Gabi's actual screen". */}
      <View className="flex-row flex-wrap items-center gap-2">
        <Badge variant="outline">
          <Text>Preview · not live</Text>
        </Badge>
        <Text variant="note">
          How {partnerName} sees it · {WHERE[step.panel] ?? 'their app'}
        </Text>
      </View>
      <Body step={step} payload={payload} />
    </View>
  );
}

/**
 * A CONTROL DRAWN AS EVIDENCE. Sixty per cent, so the shape is still legible as the button it
 * approximates while never reading as one that is waiting to be pressed.
 *
 * The frame above is already inert, so this is purely what the eye is told: the readiness capture
 * found "Did it", "Not for me" and the +3 chip indistinguishable from the live Missions card
 * eighteen inches away on the same page.
 */
function Mock({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <View className={className} style={{ opacity: 0.6 }}>
      {children}
    </View>
  );
}

function Body({ step, payload }: { step: PreviewStep; payload: Record<string, unknown> }) {
  switch (step.panel) {
    case 'mission':
      return (
        // The mission a partner sees, drawn the way the real Missions card is: the worth is a
        // `Points` chip beside the title rather than a `+3` inside the sub-line, and the second
        // action is the secondary the restyle gives it. The preview is an approximation, but the
        // things it approximates have to be the same OBJECTS, or it stops being evidence.
        <View className="bg-card border-border gap-1 rounded-md border p-3">
          <View className="flex-row items-start justify-between gap-3">
            <Text className="min-w-0 flex-1 font-semibold">{str(payload.title, step.phrasing)}</Text>
            <Mock>
              <Points value={3} />
            </Mock>
          </View>
          <Text variant="small" className="text-muted-foreground">
            from the game
          </Text>
          <Mock className="flex-row flex-wrap gap-2 pt-1">
            <Button size="sm">
              <Text>Did it</Text>
            </Button>
            <Button size="sm" variant="secondary">
              <Text>Not for me</Text>
            </Button>
          </Mock>
        </View>
      );

    case 'list_item':
      return (
        <View className="bg-card border-border flex-row flex-wrap items-center gap-3 rounded-md border p-3">
          <Mock>
            <View className="border-line-strong bg-card size-6 rounded-sm border" />
          </Mock>
          <Text className="min-w-0 grow basis-[140px]">{str(payload.text, step.phrasing)}</Text>
          {/* The AUTHOR is the one person allowed to know this row was planted, and this preview
              is the only place it is ever said. On the list itself the row carries no caption at
              all — see `List.tsx` — which is what this sentence is telling them. */}
          <Text variant="small" className="text-muted-foreground">
            · lands on the list as an ordinary item
          </Text>
        </View>
      );

    case 'date_card':
      // The face stays sealed even here. The author approved what is under it; the partner sees a
      // question mark and three icons, exactly like every other card on the wall.
      return (
        <View className="items-center gap-2">
          <View className="bg-muted border-border h-[124px] w-[110px] items-center justify-center gap-2 rounded-lg border">
            <Text variant="display" className="text-muted-foreground">
              ?
            </Text>
            <Text variant="small">{'⏱💵🧰'}</Text>
          </View>
          <Text variant="small" className="text-muted-foreground">
            sealed until scratched
          </Text>
        </View>
      );

    case 'sync_probe':
      return (
        <View className="bg-card border-border gap-1 rounded-md border p-3">
          <Badge>
            <Text>{payload.type === 'predict' ? 'Predict' : 'Compare'}</Text>
          </Badge>
          <Text className="font-semibold">{str(payload.text, step.phrasing)}</Text>
          <Text variant="small" className="text-muted-foreground">
            asked among unrelated questions
          </Text>
        </View>
      );

    case 'gratitude_nudge':
      return (
        <View className="bg-card border-border gap-1 rounded-md border p-3">
          <Text variant="small" className="text-muted-foreground">
            Today’s prompt · from the game
          </Text>
          <Text className="font-semibold">{step.phrasing}</Text>
        </View>
      );

    case 'coop_challenge':
      return (
        <View className="bg-card border-border gap-1 rounded-md border p-3">
          <Text className="font-semibold">{str(payload.title, step.phrasing)}</Text>
          <Text variant="small" className="text-muted-foreground">
            {num(payload.target, 4)} nights · {num(payload.budget, 2)} misses allowed · you both win or lose it
            together
          </Text>
        </View>
      );

    default:
      return <Text>{step.phrasing}</Text>;
  }
}

/* `traceVariant` and `traceClass` lived here and are gone with the trace BADGE: the review card
   reads the trace as a `note` line now — "Trace: low" — because a coloured pill next to the
   filled panel-label chip was two labels arguing over which the eye should take first, and a
   tan-on-maroon chip was the least legible object in the dark room. */

/** One run of the rewritten phrasing, and whether the rewrite is what put it there. */
export interface PhrasingSegment {
  text: string;
  changed: boolean;
}

/**
 * Which words a redraft changed — an LCS over whitespace-separated tokens.
 *
 * Word level rather than character level because the author is reading a sentence, not a patch:
 * "which words moved" is the question, and a character diff marks the inside of a word that merely
 * gained a comma. Quadratic, over two phrasings of a few dozen words, run once when a rewrite
 * lands — the table is tiny and it never runs in a loop.
 *
 * The segments are the AFTER text in full and in order, so joining their `text` reproduces it
 * exactly. That is the load-bearing property: a caller that ignores `changed` still renders the
 * phrasing the author is being asked to approve, whitespace and all, rather than a reconstruction
 * of it.
 */
export function diffWords(before: string, after: string): PhrasingSegment[] {
  const isSpace = (t: string) => /^\s+$/.test(t);
  const beforeWords = before.trim().split(/\s+/).filter(Boolean);
  // Splitting on a CAPTURED separator keeps the whitespace as tokens of its own, so a phrasing
  // with a line break or a double space survives the round trip unchanged.
  const tokens = after.split(/(\s+)/).filter((t) => t.length > 0);
  const afterWords = tokens.filter((t) => !isSpace(t));

  const n = beforeWords.length;
  const m = afterWords.length;
  if (!m) return [];

  // table[i][j] is the longest common run of beforeWords[i..] and afterWords[j..].
  const table: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      const row = table[i]!;
      const next = table[i + 1]!;
      row[j] = beforeWords[i] === afterWords[j] ? next[j + 1]! + 1 : Math.max(next[j]!, row[j + 1]!);
    }
  }

  // Walk the table once; every after-word NOT on the common path is one the rewrite introduced.
  const kept = new Array<boolean>(m).fill(false);
  for (let i = 0, j = 0; i < n && j < m; ) {
    if (beforeWords[i] === afterWords[j]) {
      kept[j] = true;
      i++;
      j++;
    } else if (table[i + 1]![j]! >= table[i]![j + 1]!) i++;
    else j++;
  }

  let word = 0;
  const flags = tokens.map((t) => (isSpace(t) ? false : !kept[word++]));
  // Whitespace BETWEEN two changed words is itself changed, so the underline runs across a marked
  // phrase instead of breaking at every space. Tokens strictly alternate, so a neighbour of a
  // space is always a word.
  tokens.forEach((t, k) => {
    if (isSpace(t)) flags[k] = !!flags[k - 1] && !!flags[k + 1];
  });

  // Merge neighbours that agree: fewer Text nodes, and one continuous underline per marked run.
  return tokens.reduce<PhrasingSegment[]>((out, text, k) => {
    const last = out[out.length - 1];
    if (last && last.changed === !!flags[k]) last.text += text;
    else out.push({ text, changed: !!flags[k] });
    return out;
  }, []);
}

/**
 * The rewritten phrasing with the rewrite's own words marked.
 *
 * Nested `Text` rather than separate lines because the sentence has to still read as a sentence —
 * the marking is evidence about *why* the trace badge moved, and a list of changed words would
 * make the author reassemble the meaning themselves. Underline as well as colour, so the mark
 * survives a reader who cannot tell --primary from --foreground.
 */
export function MarkedPhrasing({ before, after, className }: { before: string; after: string; className?: string }) {
  const segments = React.useMemo(() => diffWords(before, after), [before, after]);
  if (!segments.length) return <Text className={className}>{after}</Text>;
  return (
    <Text className={className}>
      {segments.map((segment, i) =>
        segment.changed ? (
          <Text key={i} className="text-primary underline">
            {segment.text}
          </Text>
        ) : (
          <Text key={i}>{segment.text}</Text>
        ),
      )}
    </Text>
  );
}
